import React from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import { LegalPage, H2, P, UL } from '../components/LegalPage';

const CachodiceTerms = () => {
  const { lang } = useLanguage();

  if (lang === 'en') {
    return (
      <LegalPage
        testid="cachodice-terms-en"
        eyebrow="CachoDice"
        title="Terms of Service"
        subtitle="Operated by Inversiones Tair SpA"
        lastUpdated="Last updated: July 2026"
        backTo="/cachodice"
        backLabel="Back · CachoDice"
      >
        <P>
          Welcome to <strong className="text-white">CachoDice</strong> (the "App"), a service
          of <strong className="text-white">Inversiones Tair SpA</strong> (tair.site). By installing
          or using the App, you accept these terms. If you do not agree, please do not use the App.
        </P>

        <H2>1. Nature of the service</H2>
        <P>
          CachoDice is a dice game for iPhone and iPad that recreates the classic Chilean game of
          Cacho (also known as Dudo or Perudo). The App offers offline single-device play against
          the CPU, local multiplayer via <strong className="text-white">Apple Multipeer
          Connectivity</strong> (a direct P2P connection between nearby devices) and a bilingual
          interface (Spanish and English).
        </P>
        <P>
          CachoDice is a recreational and entertainment product. It does not involve real-money
          betting, does not distribute prizes and is not a gambling service.
        </P>

        <H2>2. User account</H2>
        <UL items={[
          'You must be 13+ to use the App.',
          'CachoDice does not require an account or registration. Your player name is stored locally on your device.',
          'We may block or refuse service to users who abuse the App or violate these terms.',
        ]} />

        <H2>3. Free trial and in-app purchase</H2>
        <P strong><strong className="text-white">One-time purchase model:</strong></P>
        <UL items={[
          'Free trial: 7 days of full access on installation, no card required.',
          'Full version: one-time in-app purchase to keep unlimited access after the trial ends.',
        ]} />
        <UL items={[
          <>Payments are processed exclusively via the <strong className="text-white">Apple App Store</strong> (StoreKit). Apple Media Services terms apply.</>,
          'The purchase is one-time and is tied to your Apple ID; it can be restored on any device with the same Apple ID at no additional cost.',
          'We do not receive nor store any card or payment data — those data are handled by Apple under their own policy.',
          'Refunds are handled directly by Apple, not by Inversiones Tair SpA.',
        ]} />

        <H2>4. Acceptable use</H2>
        <UL items={[
          'Use the App only for personal, recreational entertainment.',
          'Do not copy, redistribute or resell the code, assets or artwork.',
          'Do not reverse-engineer or attempt to tamper with the game logic.',
          'Do not use the App to offend, harass or impersonate other players in multiplayer rooms.',
        ]} />

        <H2>5. Intellectual property</H2>
        <P>
          The code, design, illustrations, sounds and animations of CachoDice are the property of
          Inversiones Tair SpA. The traditional rules of Cacho / Dudo / Perudo belong to popular
          culture and are not claimed as our property.
        </P>
        <P>
          We grant you a personal, non-exclusive and non-transferable licence to use the App on the
          devices associated with your Apple ID.
        </P>

        <H2>6. Multiplayer and communications</H2>
        <P>
          Multiplayer matches use Apple Multipeer Connectivity: your player name and match data are
          exchanged only with the players in your same room and are never sent to or stored on any
          of our servers. We do not have access to those communications.
        </P>

        <H2>7. Availability</H2>
        <P>
          The App runs primarily offline. Because it does not depend on our servers, availability is
          tied to the operation of iOS and the Apple App Store. We may release updates via the App
          Store without prior notice.
        </P>

        <H2>8. Limitation of liability</H2>
        <P>
          The App is provided "as is". To the maximum extent permitted by law, Inversiones Tair SpA
          shall not be liable for:
        </P>
        <UL items={[
          'Interruptions of the Multipeer Connectivity service or network problems on your device.',
          'Loss of local game data due to reinstallation, device change or restore.',
          'Indirect damages arising from the use or impossibility of use of the App.',
        ]} />

        <H2>9. Modifications</H2>
        <P>
          We may update these terms. New versions will be published at{' '}
          <a href="https://tair.site/cachodice/terms" className="text-amber-300 hover:text-amber-200">
            tair.site/cachodice/terms
          </a>
          . Continued use after a change implies acceptance.
        </P>

        <H2>10. Governing law</H2>
        <P>
          These terms are governed by the laws of the country where Inversiones Tair SpA has its
          operating headquarters (Chile), without prejudice to the consumer rights of your country
          of residence.
        </P>

        <H2>11. Contact</H2>
        <P>
          For any inquiry about these terms:{' '}
          <a href="mailto:info@tair.site" className="text-amber-300 hover:text-amber-200">info@tair.site</a>
        </P>
      </LegalPage>
    );
  }

  // Spanish (default)
  return (
    <LegalPage
      testid="cachodice-terms-es"
      eyebrow="CachoDice"
      title="Términos y Condiciones de Uso"
      subtitle="Operada por Inversiones Tair SpA"
      lastUpdated="Última actualización: Julio 2026"
      backTo="/cachodice"
      backLabel="Volver · CachoDice"
    >
      <P>
        Bienvenido a <strong className="text-white">CachoDice</strong> (la "App"), un servicio
        de <strong className="text-white">Inversiones Tair SpA</strong> (tair.site). Al instalar
        o usar la App, aceptas estos términos. Si no estás de acuerdo, te pedimos no usar la App.
      </P>

      <H2>1. Naturaleza del servicio</H2>
      <P>
        CachoDice es un juego de dados para iPhone y iPad que recrea el clásico juego chileno del
        Cacho (también conocido como Dudo o Perudo). La App ofrece partidas offline de un solo
        dispositivo contra la CPU, multijugador local mediante{' '}
        <strong className="text-white">Multipeer Connectivity</strong> de Apple (una conexión
        directa P2P entre dispositivos cercanos) e interfaz bilingüe (español e inglés).
      </P>
      <P>
        CachoDice es un producto de entretenimiento recreativo. No implica apuestas de dinero real,
        no distribuye premios y no constituye un servicio de juego de azar.
      </P>

      <H2>2. Cuenta de usuario</H2>
      <UL items={[
        'Debes ser mayor de 13 años para usar la App.',
        'CachoDice no requiere cuenta ni registro. Tu nombre de jugador se guarda localmente en tu dispositivo.',
        'Podemos bloquear o rechazar el servicio a usuarios que abusen de la App o violen estos términos.',
      ]} />

      <H2>3. Prueba gratuita y compra dentro de la app</H2>
      <P strong><strong className="text-white">Modelo de compra única:</strong></P>
      <UL items={[
        'Prueba gratuita: 7 días de acceso completo desde la instalación, sin necesidad de tarjeta.',
        'Versión completa: compra única dentro de la app para mantener el acceso ilimitado una vez finalizada la prueba.',
      ]} />
      <UL items={[
        <>Los pagos se procesan exclusivamente a través del <strong className="text-white">Apple App Store</strong> (StoreKit). Aplican los términos de Apple Media Services.</>,
        'La compra es única y queda vinculada a tu Apple ID; puedes restaurarla en cualquier dispositivo con el mismo Apple ID sin costo adicional.',
        'No recibimos ni almacenamos datos de tu tarjeta ni de pago — esos datos los maneja Apple según su propia política.',
        'Los reembolsos los gestiona Apple directamente, no Inversiones Tair SpA.',
      ]} />

      <H2>4. Uso aceptable</H2>
      <P>Te comprometes a:</P>
      <UL items={[
        'Usar la App únicamente con fines personales y de entretenimiento recreativo.',
        'No copiar, redistribuir ni revender el código, los recursos ni el arte.',
        'No realizar ingeniería inversa ni intentar alterar la lógica del juego.',
        'No usar la App para ofender, hostigar o suplantar a otros jugadores en las salas multijugador.',
      ]} />

      <H2>5. Propiedad intelectual</H2>
      <P>
        El código, diseño, ilustraciones, sonidos y animaciones de CachoDice son propiedad de
        Inversiones Tair SpA. Las reglas tradicionales del Cacho / Dudo / Perudo pertenecen a la
        cultura popular y no son reclamadas como propiedad nuestra.
      </P>
      <P>
        Te otorgamos una licencia personal, no exclusiva e intransferible para usar la App en los
        dispositivos asociados a tu Apple ID.
      </P>

      <H2>6. Multijugador y comunicaciones</H2>
      <P>
        Las partidas multijugador usan Multipeer Connectivity de Apple: tu nombre de jugador y los
        datos de la partida se intercambian sólo con los jugadores de tu misma sala y no se envían
        ni almacenan en ningún servidor nuestro. No tenemos acceso a esas comunicaciones.
      </P>

      <H2>7. Disponibilidad</H2>
      <P>
        La App funciona principalmente offline. Como no depende de servidores propios, la
        disponibilidad está ligada al funcionamiento de iOS y del Apple App Store. Podemos publicar
        actualizaciones vía App Store sin previo aviso.
      </P>

      <H2>8. Limitación de responsabilidad</H2>
      <P>
        La App se ofrece "tal cual". En la medida máxima permitida por la ley, Inversiones Tair SpA
        no será responsable de:
      </P>
      <UL items={[
        'Interrupciones del servicio Multipeer Connectivity o problemas de red en tu dispositivo.',
        'Pérdida de datos locales de partida por reinstalación, cambio de dispositivo o restauración.',
        'Daños indirectos derivados del uso o imposibilidad de uso de la App.',
      ]} />

      <H2>9. Modificaciones</H2>
      <P>
        Podemos actualizar estos términos. Las versiones nuevas serán publicadas en{' '}
        <a href="https://tair.site/cachodice/terms" className="text-amber-300 hover:text-amber-200">
          tair.site/cachodice/terms
        </a>
        . El uso continuado tras una modificación implica aceptación.
      </P>

      <H2>10. Ley aplicable</H2>
      <P>
        Estos términos se rigen por las leyes del país donde Inversiones Tair SpA tiene su sede
        operativa (Chile), sin perjuicio de los derechos del consumidor de tu país de residencia.
      </P>

      <H2>11. Contacto</H2>
      <P>
        Para cualquier consulta sobre estos términos:{' '}
        <a href="mailto:info@tair.site" className="text-amber-300 hover:text-amber-200">info@tair.site</a>
      </P>
    </LegalPage>
  );
};

export default CachodiceTerms;
